import mixins from 'vue-typed-mixins';

import { E入力分類, E補助科目フラグ, Eフェーズ種別 } from '@/openapi/enum';
import {
  E集計転記科目,
  PlbscfデータRequest,
  YearMonthValue,
  データApi,
  データListItemRequest,
  勘定科目Api,
  部門Api,
} from '@/openapi/user';

import KeystrokeInputableText from '@/components/keystrokeInputableText/KeystrokeInputableText.vue';
import C決算期TableHeader from '@/components/決算期TableHeader/決算期TableHeader.vue';
import C保存Button from '@/components/保存Button/保存Button.vue';

import { KeystrokeInputableTextId } from '@/components/keystrokeInputableText/KeystrokeInputableText';

import { InputableTextMixin } from '@/components/inputableText/InputableText';
import KeystrokeInputableTextMixin from '@/entries/user/mixins/KeystrokeInputableTextMixin';
import MoneyUnitLabelMixin from '@/entries/user/mixins/MoneyUnitLabelMixin';
import 計画書Mixin from '@/entries/user/mixins/計画書Mixin';
import 事業所Mixin from '@/entries/user/mixins/事業所Mixin';

import UserApiUtility from '@/classes/common/UserApiUtility';
import 返済明細Service, {
  CellTagType,
  ListItemType,
  返済明細,
  返済明細Cell,
  返済明細ListItem,
} from '@/classes/service/計画/返済明細Service';
import OldLoanMixin from '@/entries/user/mixins/OldLoanMixin';

export default mixins(
  InputableTextMixin,
  KeystrokeInputableTextMixin,
  MoneyUnitLabelMixin,
  計画書Mixin,
  事業所Mixin,
  OldLoanMixin
).extend({
  name: 'C返済明細',

  components: {
    KeystrokeInputableText,
    C決算期TableHeader,
    C保存Button,
  },

  data(): {
    readonly cellTagType: typeof CellTagType;
    api: {
      d: データApi;
      k: 勘定科目Api;
      b: 部門Api;
    };
    service: 返済明細Service;
    v返済明細: 返済明細 | null;
    isEditing: boolean;
  } {
    return {
      cellTagType: CellTagType,
      api: {
        d: UserApiUtility.ogApi(データApi),
        k: UserApiUtility.ogApi(勘定科目Api),
        b: UserApiUtility.ogApi(部門Api),
      },
      service: new 返済明細Service(Eフェーズ種別.計画フェーズ),
      v返済明細: null,
      isEditing: false,
    };
  },

  computed: {
    colCountInRow(): number {
      return this.service.getColCountInRow;
    },
    map(): (KeystrokeInputableTextId | null)[][] {
      if (this.v返済明細 === null) return [];

      const canInput = (type: CellTagType | undefined): boolean =>
        type !== undefined &&
        type !== CellTagType.currency &&
        type !== CellTagType.textLeft &&
        type !== CellTagType.textRight;

      return this.v返済明細.tables.flatMap((table, i) =>
        table.list.map((item, j) => item.cells.map((cell, k) => (canInput(cell?.tagType) ? { i, j, k } : null)))
      );
    },
  },

  filters: {
    rowCss(type: ListItemType): string {
      switch (type) {
        case ListItemType.新規借入:
          return 'shinki';
        case ListItemType.元本返済:
          return 'ganpon';
        case ListItemType.残高:
          return 'zandaka';
        default:
          return '';
      }
    },
    cellCss(cell: 返済明細Cell | null): string {
      if (cell === null) return '';

      const ret: string[] = [];
      if (cell.rowspan > 1) ret.push('white');
      if (cell.tagType !== CellTagType.textLeft) ret.push('text-right');
      return ret.join(' ');
    },
  },

  async created(): Promise<void> {
    await this.load();
    this.setFirstKeystrokeId(this.map);
  },

  methods: {
    cellValue(cell: 返済明細Cell): string | number {
      // ymInputが存在、かつ値が設定されている場合はymInputを返却
      // それ以外はfixを返却
      return cell.ymInput?.value ?? cell.fix;
    },
    onChange(value: number, item: 返済明細ListItem, cell: 返済明細Cell): void {
      if (this.v返済明細 === null) return;
      this.isEditing = true;

      // ymInputがある場合は値を設定
      if (cell.ymInput !== undefined) cell.ymInput.value = value;

      // Cデータに存在しない場合は追加
      const 借入 = item.借入!;
      let targets: YearMonthValue[] = [];
      switch (item.type) {
        case ListItemType.新規借入:
          if (借入.新規借入 === undefined) 借入.新規借入 = [];
          targets = 借入.新規借入;
          break;
        case ListItemType.元本返済:
          targets = 借入.元本返済;
          break;
        case ListItemType.利息:
          targets = 借入.利息;
          break;
        default:
          return;
      }

      if (!targets.some((t) => t.ym === cell.ym)) {
        targets.push({ ym: cell.ym!, value: value });
      }

      // 再構築
      this.service.initialize返済明細Tables(this.v返済明細);
    },
    async load(): Promise<void> {
      this.isEditing = false;

      const データOptions = {
        params: {
          sj: '返済明細',
        },
      };
      const 部門Request = this.api.b.list部門(this.事業所.pk);
      const 勘定科目Request = this.api.k.list勘定科目(this.事業所.pk, E補助科目フラグ.通常);
      const データRequest = this.api.d.listデータ(
        this.計画書.pk,
        this.service.getRelationalE入力分類s(),
        undefined,
        データOptions
      );
      const responses = await Promise.all([部門Request, 勘定科目Request, データRequest]);
      this.service.set部門s = responses[0].data;
      this.service.set勘定科目s = responses[1].data;
      this.v返済明細 = this.service.parse(responses[2].data);

      const oldData = this.service.createTデータOldLoans(responses[2].data);
      this.getOldLoans(oldData, this.v返済明細);
    },
    async save(): Promise<void> {
      if (this.v返済明細 === null || !this.isEditing) return;

      // 金融機関借入/役員借入保存
      const targetE入力分類s = [E入力分類.金融機関借入, E入力分類.役員借入];
      const データRequests: データListItemRequest[] = targetE入力分類s.flatMap((v入力分類) => {
        const v返済明細 = this.v返済明細!;
        const list = v入力分類 === E入力分類.金融機関借入 ? v返済明細.v金融機関借入.list : v返済明細.v役員借入.list;
        return list.map((item) => {
          const { データpk, 番号, フェーズ種別, ...cデータ } = item;
          return {
            pk: データpk!,
            c入力分類: v入力分類,
            c番号: 番号!,
            cフェーズ種別: フェーズ種別!,
            cデータ: JSON.stringify(cデータ),
          };
        });
      });
      this.updateRequestBulkSave(データRequests);
      await this.api.d.bulkSaveデータ(this.計画書.pk, データRequests);

      await this.load();
    },
    async savePlbscf(): Promise<void> {
      if (this.v返済明細 === null) return;

      // PLBSCF転記（対象の勘定科目が存在する場合のみ）
      const 勘定科目 = this.service.get勘定科目s.find((item) => item.c集計転記科目 === E集計転記科目.支払利息);
      if (勘定科目 === undefined) return;

      const 部門Pk = this.service.get部門s[0].pk;
      const yearValues = this.service.get利息YearValuesForPlbscf(this.v返済明細.tables, 部門Pk, 勘定科目.pk);
      const plbscfRequest: PlbscfデータRequest = {
        cフェーズ種別: Eフェーズ種別.計画フェーズ,
        yearValues: yearValues,
        saveOldValues: true,
      };
      await this.api.d.savePlbscfデータ(this.計画書.pk, plbscfRequest);
    },
  },
});
